import React, { forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  type CustomFieldValidatorState,
  ErrorTooltip,
  moment,
  type Nil,
  PuiTextArea,
  Text,
  useFields,
} from '@pbt/pbt-ui-components'

import { OutlinedSelect } from '~/components/elements/OutlinedSelect/OutlinedSelect'
import type { DataHandle } from '~/types'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import { useWellnessPlanCancelReasons } from '../../wellness-plans/wellnessPlanUtils'

const StyledAlert = styled(Alert)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  border: solid 1px;
  border-color: ${({ theme }) => theme.palette.info.light};
`

const StyledAlertText = styled(Text)`
  && {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`

export enum CancelTiming {
  IMMEDIATELY = 'immediately',
  END_OF_TERM = 'end_of_term',
}

export type CancellationData = {
  cancelTiming: CancelTiming
  notes: string
  reason: string
}

interface CancelMembershipDetailsProps {
  planEndDate?: string | Nil
}

const CancelMembershipDetails = forwardRef<
  DataHandle<CancellationData> | undefined,
  CancelMembershipDetailsProps
>(function CancelMembershipDetails({ planEndDate }, ref) {
  const { t } = useTranslation(['Common', 'Memberships', 'Validations'])

  const timezone = useCurrentBusinessTimezone()

  const WellnessPlanCancelReasons: string[] = useWellnessPlanCancelReasons()

  const validateNotesWhenOtherReasonSelected = ({
    state,
  }: CustomFieldValidatorState) => {
    // Only require notes when "Other" is selected
    if (state?.reason !== t('Common:OTHER')) {
      return true
    }
    return state?.notes?.trim().length > 0
  }

  const {
    fields: { cancelTiming, reason, notes },
    validate,
  } = useFields(
    [
      { name: 'cancelTiming', initialValue: CancelTiming.IMMEDIATELY },
      {
        name: 'reason',
        initialValue: '',
        validators: ['required'],
        messages: {
          required: t('Validations:SELECT_CANCELLATION_REASON'),
        },
      },
      {
        name: 'notes',
        initialValue: '',
        validators: [
          {
            validator: validateNotesWhenOtherReasonSelected,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('Validations:ENTER_CANCELLATION_NOTES'),
        },
      },
    ],
    false,
  )

  const FieldsLabels = {
    REASON_FOR_CANCELLATION: `${t('Memberships:CANCEL_PLAN.REASON_FOR_CANCELLATION')}*`,
    CANCELLATION_NOTES: `${t('Memberships:CANCEL_PLAN.CANCELLATION_NOTES')}*`,
  }

  useImperativeHandle(ref, () => ({
    validate,
    get: () => ({
      cancelTiming: cancelTiming.value,
      reason: reason.value,
      notes: notes.value,
    }),
  }))

  const momentEndDate = planEndDate ? moment.tz(planEndDate, timezone) : null
  const formattedEndDate = momentEndDate?.isValid()
    ? momentEndDate.format('MM/DD/YYYY')
    : ''
  const showCancelAtEndOfTermBanner =
    cancelTiming.value === CancelTiming.END_OF_TERM && formattedEndDate

  return (
    <Grid container direction="column" p={3}>
      <Text strong mb={1} variant="subheading3">
        {t('Memberships:CANCEL_PLAN.CANCEL_TIMING')}
      </Text>
      <RadioGroup
        row
        value={cancelTiming.value}
        onChange={(_, value) => cancelTiming.setValue(value as CancelTiming)}
      >
        <FormControlLabel
          control={<Radio />}
          label={t('Memberships:CANCEL_PLAN.IMMEDIATELY')}
          value={CancelTiming.IMMEDIATELY}
        />
        <FormControlLabel
          control={<Radio />}
          label={t('Memberships:CANCEL_PLAN.AT_END_OF_TERM')}
          value={CancelTiming.END_OF_TERM}
        />
      </RadioGroup>
      {showCancelAtEndOfTermBanner && (
        <StyledAlert severity="info">
          <StyledAlertText strong variant="body2">
            {t('Memberships:CANCEL_PLAN.PLAN_WILL_CANCEL_ON', {
              date: formattedEndDate,
            })}
          </StyledAlertText>
          <StyledAlertText variant="body2">
            {t('Memberships:CANCEL_PLAN.BENEFITS_REMAIN_ACTIVE')}
          </StyledAlertText>
        </StyledAlert>
      )}
      <Text strong mb={1} mt={3} variant="subheading3">
        {FieldsLabels.REASON_FOR_CANCELLATION}
      </Text>
      <ErrorTooltip message={reason.message} open={!reason.valid}>
        <FormControl fullWidth error={!reason.valid}>
          <OutlinedSelect
            id="cancel-reason-select"
            label={t('Memberships:CANCEL_PLAN.REASON_LABEL')}
            options={WellnessPlanCancelReasons}
            value={reason.value}
            onChange={(value) => reason.setValue(value)}
          />
        </FormControl>
      </ErrorTooltip>
      <Grid container item mt={1}>
        <PuiTextArea
          multiline
          field={notes}
          placeholder={FieldsLabels.CANCELLATION_NOTES}
        />
      </Grid>
    </Grid>
  )
})

export default CancelMembershipDetails
